// src/pages/admin/manager/formularios/FormulariosPage.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stack,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import WarningIcon from '@mui/icons-material/Warning';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useUsuarios, type Usuario } from '../../../../hooks/useUsuarios';
import {
  listFormularios,
  deleteFormulario,
  updateFormulario,
  duplicarFormulario,
  activarFormulario,
  desactivarFormulario,
  getFormularioMetrics,
  type Formulario,
} from '../../../../api/formularios';
import FormularioEditorDialog from '../../../../components/formularios/FormularioEditorDialog';
import AsignarFormularioDialog from '../../../../components/formularios/AsignarFormularioDialog';
import FormsPlantillasDropDown from '../../../../components/formularios/FormsPlantillasDropDown';

export default function FormulariosPage() {
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [formularios, setFormularios] = useState<Formulario[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedFormulario, setSelectedFormulario] = useState<Formulario | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFormularioId, setEditorFormularioId] = useState<number | undefined>();
  const [editorTemplate, setEditorTemplate] = useState<Formulario | null>(null);
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [asignarFormularioId, setAsignarFormularioId] = useState<number | undefined>();
  const [formulariosMetrics, setFormulariosMetrics] = useState<
    Record<number, { completados: number; total: number }>
  >({});
  const [metricsLoadedIds, setMetricsLoadedIds] = useState<Set<number>>(new Set());
  const metricsLoadIdRef = useRef(0);

  // Filtros
  const [filtroTitulo, setFiltroTitulo] = useState('');
  const [filtroDepartamento, setFiltroDepartamento] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroProceso, setFiltroProceso] = useState('');
  const [filtroCreadoPor, setFiltroCreadoPor] = useState<Usuario | null>(null);
  const [filtroFechaCreacionDesde, setFiltroFechaCreacionDesde] = useState<Dayjs | null>(null);
  const [filtroFechaCreacionHasta, setFiltroFechaCreacionHasta] = useState<Dayjs | null>(null);
  // Ocultar formularios eliminados en la lista (por defecto activo)
  const [hideDeleted, setHideDeleted] = useState(true);

  // Obtener lista de usuarios (solo staff, activos)
  const { rows: usuariosStaff } = useUsuarios({
    sinClientes: true,
    soloActivos: true,
    auto: true,
  });

  // Filtrar solo staff (excluir profesor, cliente, consultor)
  const staffUsuarios = useMemo(() => {
    const staffRoles = ['hr', 'att_familias', 'att_alumnado', 'att_profesorado', 'facturacion', 'admisiones', 'admin'];
    return usuariosStaff
      .filter((u) => staffRoles.includes(u.rol))
      .sort((a, b) => {
        const nameA = a.nombreCompleto || a.email || '';
        const nameB = b.nombreCompleto || b.email || '';
        return nameA.localeCompare(nameB);
      });
  }, [usuariosStaff]);

  // Departamentos disponibles (roles)
  const departamentos = [
    { value: 'hr', label: 'HR' },
    { value: 'att_familias', label: 'Atención Familias' },
    { value: 'att_alumnado', label: 'Atención Alumnado' },
    { value: 'att_profesorado', label: 'Atención Profesorado' },
    { value: 'facturacion', label: 'Facturación' },
    { value: 'admisiones', label: 'Admisiones' },
  ];

  // Estados disponibles
  const estados = [
    { value: 'borrador', label: 'Borrador' },
    { value: 'activo', label: 'Activo' },
    { value: 'desactivado', label: 'Desactivado' },
    { value: 'cerrado', label: 'Cerrado' },
  ];

  // Procesos disponibles
  const procesos = [
    { value: 'completo', label: 'Completo' },
    { value: 'incompleto', label: 'Incompleto' },
  ];

  const loadMetricsForFormularios = useCallback(async (items: Formulario[], loadId: number) => {
    if (!items.length) {
      setFormulariosMetrics({});
      setMetricsLoadedIds(new Set());
      return;
    }

    const metricsMap: Record<number, { completados: number; total: number }> = {};
    const loadedIds = new Set<number>();
    let index = 0;
    const concurrency = 5;

    const worker = async () => {
      while (index < items.length) {
        const current = items[index++];
        try {
          const metrics = await getFormularioMetrics(current.id);
          metricsMap[current.id] = {
            completados: metrics.general?.completados || 0,
            total: metrics.general?.asignacionesTotales || 0,
          };
        } catch (error) {
          metricsMap[current.id] = { completados: 0, total: 0 };
        } finally {
          loadedIds.add(current.id);
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));

    if (metricsLoadIdRef.current !== loadId) return;

    setFormulariosMetrics(metricsMap);
    setMetricsLoadedIds(loadedIds);
  }, []);

  const loadFormularios = useCallback(async () => {
    setLoading(true);
    try {
      // Solo formularios (excluye evaluaciones: esEvaluacion = false)
      const { data } = await listFormularios({ limit: 100, esEvaluacion: false });
      const formulariosNoEvaluacion = data || [];
      setFormularios(formulariosNoEvaluacion);
      setFormulariosMetrics({});
      setMetricsLoadedIds(new Set());
      const loadId = metricsLoadIdRef.current + 1;
      metricsLoadIdRef.current = loadId;
      loadMetricsForFormularios(formulariosNoEvaluacion, loadId);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar formularios', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, loadMetricsForFormularios]);

  useEffect(() => {
    loadFormularios();
  }, [loadFormularios]);

  const handleDelete = async (formulario: Formulario) => {
    if (formulario.esPlantilla) {
      const ok = window.confirm(
        'Este formulario está marcado como Plantilla. ¿Quieres quitarlo de Plantillas? El formulario no será eliminado.'
      );
      if (!ok) return;
      try {
        await updateFormulario(formulario.id, { esPlantilla: false });
        enqueueSnackbar('Plantilla eliminada', { variant: 'success' });
        loadFormularios();
        // Notify templates dropdown to reload
        window.dispatchEvent(new CustomEvent('plantillas:changed', { detail: { id: formulario.id } }));
      } catch (error: any) {
        enqueueSnackbar('Error al eliminar plantilla', { variant: 'error' });
      }
    } else {
      if (!window.confirm('¿Estás seguro de eliminar este formulario?')) return;
      try {
        await deleteFormulario(formulario.id);
        enqueueSnackbar('Formulario eliminado', { variant: 'success' });
        loadFormularios();
        // Notify templates dropdown in case the deleted form was shown there
        window.dispatchEvent(new CustomEvent('plantillas:changed', { detail: { id: formulario.id } }));
      } catch (error: any) {
        enqueueSnackbar('Error al eliminar formulario', { variant: 'error' });
      }
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await duplicarFormulario(id);
      enqueueSnackbar('Formulario duplicado', { variant: 'success' });
      loadFormularios();
    } catch (error: any) {
      enqueueSnackbar('Error al duplicar formulario', { variant: 'error' });
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activarFormulario(id);
      enqueueSnackbar('Formulario activado', { variant: 'success' });
      loadFormularios();
    } catch (error: any) {
      enqueueSnackbar('Error al activar formulario', { variant: 'error' });
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await desactivarFormulario(id);
      enqueueSnackbar('Formulario desactivado', { variant: 'success' });
      loadFormularios();
    } catch (error: any) {
      enqueueSnackbar('Error al desactivar formulario', { variant: 'error' });
    }
  };

  const handleEdit = (id: number) => {
    setEditorFormularioId(id);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditorFormularioId(undefined);
    setEditorTemplate(null);
    setEditorOpen(true);
  };

  const handleAssign = (id: number) => {
    setAsignarFormularioId(id);
    setAsignarOpen(true);
  };

  // Open editor in 'create' mode prefilled with template data (do not duplicate immediately)
  const handleUseTemplate = (template: Formulario) => {
    setEditorFormularioId(undefined);
    setEditorTemplate(template);
    setEditorOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, formulario: Formulario) => {
    setMenuAnchor(event.currentTarget);
    setSelectedFormulario(formulario);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedFormulario(null);
  };

  const handleMetricas = (id: number) => {
    const path = `/admin/manager/formularios/${id}/metricas`;
    navigate(path);
  };

  const getEstadoChipSx = (estado: string) => {
    switch (estado) {
      case 'eliminado':
        return { backgroundColor: '#BD0000', color: '#FFFFFF', fontWeight: 'medium' };
      case 'borrador':
        return { backgroundColor: '#FB82CD', color: '#FFFFFF', fontWeight: 'medium' };
      case 'desactivado':
        return { backgroundColor: '#CBCBCB', color: '#4A4949', fontWeight: 'medium' };
      case 'activo':
        return { backgroundColor: (theme: any) => theme.palette.success.main, color: '#FFFFFF', fontWeight: 'medium' };
      case 'cerrado':
        return { backgroundColor: (theme: any) => theme.palette.info.main, color: '#FFFFFF', fontWeight: 'medium' };
      case 'completado':
        return { backgroundColor: (theme: any) => theme.palette.success.main, color: '#FFFFFF', fontWeight: 'medium' };
      default:
        return {};
    }
  };

  const calcularDiasRestantes = (fechaLimite: string | undefined): number | null => {
    if (!fechaLimite) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaLim = new Date(fechaLimite);
    fechaLim.setHours(0, 0, 0, 0);
    const diffTime = fechaLim.getTime() - hoy.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const tieneFiltrosActivos =
    filtroTitulo !== '' ||
    filtroDepartamento !== '' ||
    filtroEstado !== '' ||
    filtroProceso !== '' ||
    filtroCreadoPor !== null ||
    filtroFechaCreacionDesde !== null ||
    filtroFechaCreacionHasta !== null;

  const limpiarFiltros = () => {
    setFiltroTitulo('');
    setFiltroDepartamento('');
    setFiltroEstado('');
    setFiltroProceso('');
    setFiltroCreadoPor(null);
    setFiltroFechaCreacionDesde(null);
    setFiltroFechaCreacionHasta(null);
  };

  const filtered = formularios.filter((f) => {
    // Ocultar eliminados si corresponde
    if (hideDeleted && f.estado === 'eliminado') return false;

    // Filtro por título
    if (filtroTitulo) {
      const s = filtroTitulo.toLowerCase();
      if (!f.titulo.toLowerCase().includes(s)) return false;
    }

    // Filtro por departamento
    if (filtroDepartamento) {
      if (f.departamento !== filtroDepartamento) return false;
    }

    // Filtro por estado
    if (filtroEstado) {
      if (f.estado !== filtroEstado) return false;
    }

    // Filtro por creado por
    if (filtroCreadoPor) {
      if (f.creadoPor !== filtroCreadoPor.id) return false;
    }

    // Filtro por proceso
    if (filtroProceso) {
      const hasMetrics = metricsLoadedIds.has(f.id);
      if (hasMetrics) {
        const metrics = formulariosMetrics[f.id] || { completados: 0, total: 0 };
        const isCompleto = metrics.total > 0 && metrics.completados === metrics.total;
        if (filtroProceso === 'completo' && !isCompleto) return false;
        if (filtroProceso === 'incompleto' && (metrics.total === 0 || isCompleto)) return false;
      }
    }

    // Filtro por fecha de creación (rango)
    if (filtroFechaCreacionDesde || filtroFechaCreacionHasta) {
      if (!f.createdAt) return false;
      const fechaCreacion = dayjs(f.createdAt);
      if (filtroFechaCreacionDesde && fechaCreacion.isBefore(filtroFechaCreacionDesde, 'day')) {
        return false;
      }
      if (filtroFechaCreacionHasta && fechaCreacion.isAfter(filtroFechaCreacionHasta, 'day')) {
        return false;
      }
    }

    return true;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Stack spacing={2}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Gestión de formularios
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            Crear formulario
          </Button>
        </Stack>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Filtros
          </Typography>
          {/* Fila 1: Filtros generales */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Por título"
                size="small"
                fullWidth
                value={filtroTitulo}
                onChange={(e) => setFiltroTitulo(e.target.value)}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Por departamento</InputLabel>
                <Select
                  value={filtroDepartamento}
                  label="Por departamento"
                  onChange={(e) => setFiltroDepartamento(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {departamentos.map((dept) => (
                    <MenuItem key={dept.value} value={dept.value}>
                      {dept.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Por estado</InputLabel>
                <Select
                  value={filtroEstado}
                  label="Por estado"
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estados.map((est) => (
                    <MenuItem key={est.value} value={est.value}>
                      {est.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Por proceso</InputLabel>
                <Select
                  value={filtroProceso}
                  label="Por proceso"
                  onChange={(e) => setFiltroProceso(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {procesos.map((proc) => (
                    <MenuItem key={proc.value} value={proc.value}>
                      {proc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                options={staffUsuarios}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.nombreCompleto || option.email || '';
                }}
                value={filtroCreadoPor}
                onChange={(_, newValue) => {
                  // Solo aceptar Usuario, no strings libres
                  if (newValue && typeof newValue === 'object') {
                    setFiltroCreadoPor(newValue);
                  } else {
                    setFiltroCreadoPor(null);
                  }
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Creado por"
                    size="small"
                    sx={{ mb: 0 }}
                  />
                )}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) => {
                    const searchText = params.inputValue.toLowerCase();
                    const nombre = (option.nombreCompleto || '').toLowerCase();
                    const email = (option.email || '').toLowerCase();
                    return nombre.includes(searchText) || email.includes(searchText);
                  });
                  return filtered;
                }}
              />
            </Grid>
          </Grid>

          {/* Fila 2: Filtros de fecha */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <DatePicker
              label="Creados desde"
              value={filtroFechaCreacionDesde}
              onChange={(newValue) => setFiltroFechaCreacionDesde(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
            <Typography variant="body2" color="text.secondary">
              hasta
            </Typography>
            <DatePicker
              label="Creados hasta"
              value={filtroFechaCreacionHasta}
              onChange={(newValue) => setFiltroFechaCreacionHasta(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
            {tieneFiltrosActivos && (
              <Button
                startIcon={<ClearIcon />}
                onClick={limpiarFiltros}
                variant="outlined"
                size="small"
              >
                Limpiar
              </Button>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={hideDeleted}
                  onChange={(e) => setHideDeleted(e.target.checked)}
                  sx={{ color: 'red', '&.Mui-checked': { color: 'red' } }}
                />
              }
              label={<Typography sx={{ color: 'red', fontWeight: 'medium' }}>Ocultar Formularios eliminados</Typography>}
              sx={{ ml: 2 }}
            />
          </Stack>
        </Paper>

        <Box sx={{ mt: 2 }}>
          <FormsPlantillasDropDown
            onUse={handleUseTemplate}
            onEdit={(t) => handleEdit(t.id)}
            onDuplicate={(t) => handleDuplicate(t.id)}
            onActivate={(t) => handleActivate(t.id)}
            onDeactivate={(t) => handleDeactivate(t.id)}
          />
        </Box>

        <Paper sx={{ border: '1px solid black', borderRadius: '10px', overflow: 'hidden' }}>
          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Fecha de creación</TableCell>
                  <TableCell>Creado por</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Departamento</TableCell>
                  <TableCell>Proceso</TableCell>
                  <TableCell>Fecha límite</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay formularios
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((formulario: Formulario) => {
                    const hasMetrics = metricsLoadedIds.has(formulario.id);
                    const metrics = formulariosMetrics[formulario.id] || {
                      completados: 0,
                      total: 0,
                    };
                    const isCompleto = metrics.total > 0 && metrics.completados === metrics.total;
                    const procesoText = hasMetrics
                      ? metrics.total > 0
                        ? `${isCompleto ? 'Completo' : 'Incompleto'} ${metrics.completados}/${metrics.total}`
                        : '-'
                      : 'Cargando...';
                    const diasRestantes = calcularDiasRestantes(formulario.fechaLimite);
                    const mostrarAdvertencia =
                      diasRestantes !== null && diasRestantes <= 2 && diasRestantes >= 0;

                    return (
                      <TableRow key={formulario.id}>
                        <TableCell>{formulario.titulo}</TableCell>
                        <TableCell>
                          {formulario.createdAt
                            ? dayjs(formulario.createdAt).format('DD/MM/YYYY')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {formulario.creadoPorUsuario?.nombreCompleto || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formulario.estado}
                            size="small"
                            sx={getEstadoChipSx(formulario.estado)}
                          />
                        </TableCell>
                        <TableCell>{formulario.departamento || '-'}</TableCell>
                        <TableCell>
                          {hasMetrics ? (
                            metrics.total > 0 ? (
                              <Chip
                                label={procesoText}
                                size="small"
                                sx={{
                                  backgroundColor: isCompleto ? '#44A623' : '#FF9148',
                                  color: 'white',
                                  fontWeight: 'medium',
                                  borderRadius: '16px',
                                  minWidth: '100px',
                                }}
                              />
                            ) : (
                              '-'
                            )
                          ) : (
                            <Chip
                              label={procesoText}
                              size="small"
                              sx={{
                                backgroundColor: '#BDBDBD',
                                color: 'white',
                                fontWeight: 'medium',
                                borderRadius: '16px',
                                minWidth: '100px',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>
                              {formulario.fechaLimite
                                ? new Date(formulario.fechaLimite).toLocaleDateString()
                                : '-'}
                            </span>
                            {mostrarAdvertencia && (
                              <Tooltip
                                title={`Quedan ${diasRestantes} día${diasRestantes !== 1 ? 's' : ''} para que este formulario expire`}
                              >
                                <WarningIcon sx={{ color: 'red', fontSize: '20px' }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleMetricas(formulario.id)}
                            title="Ver métricas"
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleAssign(formulario.id)}
                            title="Asignar"
                          >
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, formulario)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
          {selectedFormulario && (
            <>
              <MenuItem
                onClick={() => {
                  handleEdit(selectedFormulario.id);
                  handleMenuClose();
                }}
              >
                <EditIcon sx={{ mr: 1 }} fontSize="small" />
                Editar
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDuplicate(selectedFormulario.id);
                  handleMenuClose();
                }}
              >
                <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
                Duplicar
              </MenuItem>
              {selectedFormulario.estado === 'activo' ? (
                <MenuItem
                  onClick={() => {
                    handleDeactivate(selectedFormulario.id);
                    handleMenuClose();
                  }}
                >
                  <StopIcon sx={{ mr: 1 }} fontSize="small" />
                  Desactivar
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() => {
                    handleActivate(selectedFormulario.id);
                    handleMenuClose();
                  }}
                >
                  <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
                  Activar
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  if (selectedFormulario) {
                    handleDelete(selectedFormulario);
                  }
                  handleMenuClose();
                }}
              >
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                Eliminar
              </MenuItem>
            </>
          )}
        </Menu>

        <FormularioEditorDialog
          open={editorOpen}
          formularioId={editorFormularioId}
          template={editorTemplate}
          onClose={() => {
            setEditorOpen(false);
            setEditorFormularioId(undefined);
            setEditorTemplate(null);
          }}
          onSaved={async () => {
            await loadFormularios();
            setEditorTemplate(null);
          }}
        />

        <AsignarFormularioDialog
          open={asignarOpen}
          formularioId={asignarFormularioId!}
          onClose={() => {
            setAsignarOpen(false);
            setAsignarFormularioId(undefined);
          }}
          onSaved={loadFormularios}
        />
      </Stack>
    </LocalizationProvider>
  );
}
